/**
 * The in-app path a launch URL points at, or null if it points at nothing.
 *
 * Native gives us the raw URL the app was opened with, in either of the two
 * shapes the app is registered for:
 *
 *   tago://hosting/new-listing/details?vehicleId=abc
 *   https://app.tago.rent/hosting/new-listing/details?vehicleId=abc
 *
 * Both have to come out as `/hosting/new-listing/details?vehicleId=abc`, which
 * is what expo-router's `replace` expects. Kept as a pure function so the
 * parsing is testable without a device — the routing bug it exists for only
 * reproduces on a cold start, which is the hardest thing to iterate on.
 */
export function deepLinkPathFromUrl(url: string): string | null {
  const trimmed = url.trim()
  if (!trimmed) return null

  // Expo Dev Client opens a development bundle through the app's registered
  // scheme, e.g. `tagopreview://expo-development-client/?url=…`.  It is a
  // loader instruction, not a route in Tago.  Treating it as `/expo-
  // development-client` lets a cold-start restore race navigate a signed-in
  // user to a non-existent screen after Metro has already done its job.
  //
  // Check the raw URL before the generic custom-scheme branch below, where
  // the bootstrap host intentionally becomes the first path segment.
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\/expo-development-client(?:[/?#]|$)/i.test(trimmed)) {
    return null
  }

  // Custom scheme: everything after `scheme://` is already the path. The URL
  // parser would read the first segment as a host and drop it.
  const customScheme = trimmed.match(/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\/(.*)$/)
  const isHttp = /^https?:\/\//i.test(trimmed)

  let pathWithQuery: string
  if (isHttp) {
    try {
      const parsed = new URL(trimmed)
      pathWithQuery = `${parsed.pathname}${parsed.search}`
    } catch {
      return null
    }
  } else if (customScheme) {
    // Both `tago://trips` and `tago:///trips` are emitted in the wild (the
    // latter is what Android's `am start -d` and several push providers use).
    // Do not turn that second form into `//trips`: Expo Router treats a
    // protocol-relative route as external and hands it to Chrome instead of
    // opening the trip inside Tago.
    pathWithQuery = `/${(customScheme[1] ?? '').replace(/^\/+/, '')}`
  } else {
    return null
  }

  // `tago://` alone, or a bare origin, is not a destination.
  const [path] = pathWithQuery.split('?')
  if (!path || path === '/') return null

  return pathWithQuery.startsWith('/') ? pathWithQuery : `/${pathWithQuery}`
}

/**
 * Expo Router's native-intent hook can receive either the raw URL or an
 * already-stripped path. Canonicalize both before the router decides whether a
 * route is internal. In particular, `//checkin/...` is protocol-relative to a
 * browser but means `/checkin/...` in an Android custom-scheme handoff.
 */
export function nativeIntentPathFromPath(path: string): string | null {
  const trimmed = path.trim()
  if (!trimmed) return null

  const parsedUrlPath = deepLinkPathFromUrl(trimmed)
  if (parsedUrlPath) return parsedUrlPath

  // A URL that deliberately parses to null (currently the Expo Dev Client
  // bootstrap URL) is not a Tago navigation target.
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(trimmed)) return null

  return `/${trimmed.replace(/^\/+/, '')}`
}
